import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {AuthService} from '../../services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
  fadeInLeftOnEnterAnimation,
  fadeInOnEnterAnimation,
  fadeInUpBigOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  fadeOutRightOnLeaveAnimation
} from 'angular-animations';
import {UserInfoModel} from '../../services/models/user-info.model';
import {UserService} from '../../services/user.service';
import {Subscription} from 'rxjs';
import {EventsService} from '../../services/events.service';
import {ProgressModel} from '../../services/models/progress.model';
import {ProgressService} from '../../services/progress.service';
import {environment} from '../../../environments/environment';
import {AnalyticsService} from '../../services/analytics.service';
import {Platform} from '@ionic/angular';
import {TasksModel} from '../tasks/models/tasks.model';
import {TasksService} from '../tasks/tasks.service';

@Component({
  selector: 'app-home-multi-page',
  templateUrl: 'home-multi.page.html',
  styleUrls: ['home-multi.page.scss'],
  animations: [
    fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
    fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
    fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
    fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
    fadeInUpBigOnEnterAnimation({anchor: 'enterUp', duration: 300}),
  ],
  // encapsulation: ViewEncapsulation.None,
})
export class HomeMultiPage implements OnInit, OnDestroy {

  subscriptions: Subscription[] = [];

  environment = null;
  menuVisible = false;

  userInfo: UserInfoModel = null;
  userInfoPromise: Promise<UserInfoModel> = null;
  userInfoValid = false;
  userType = null;

  progress: ProgressModel = null;
  progressPromise: Promise<ProgressModel> = null;
  progressValid = false;

  upcomingEventsList: TasksModel[] = null;
  upcomingEventsPromise: Promise<TasksModel[]> = null;
  upcomingEventsValid = false;

  platform = 'website';
  toggleSectionId = null;
  isOpened = false;
  interval = null;

  socialBlock = false;
  socialUrl: string = null;

  searchEnabled = false;
  socialEnabled = false;
  leaderboardEnabled = false;
  meetingPulse = false;

  accreditationTemplate = null;
  pmiJournalTemplate = null;

  numbers = {
    'number-1': 1,
    'number-2': 2,
    'number-3': 3,
    'number-4': 4,
    'number-5': 5,
  };

  colours = {
    'number-1': {
      primary: '#388493',
      secondary: '#A6D4DD',
    },
    'number-2': {
      primary: '#273A66',
      secondary: '#94A8D6',
    },
    'number-3': {
      primary: '#3669AF',
      secondary: '#A9C2E4',
    },
    'number-4': {
      primary: '#3F2B65',
      secondary: '#AD98D3',
    },
    'number-5': {
      primary: '#8C326A',
      secondary: '#DDA1C6',
    },
    31: {
      primary: '#0E3049',
      secondary: '#71B3E4',
    },
    32: {
      primary: '#CC6619',
      secondary: '#F3C19B',
    },
    33: {
      primary: '#A27D64',
      secondary: '#DACBC1',
    },
    34: {
      primary: '#2F8FAB',
      secondary: '#A3D7E6',
    },
    73: {
      primary: '#0A33B4',
      secondary: '#009AFF',
    },
    74: {
      primary: '#793D99',
      secondary: '#CD6EFF',
    },
    75: {
      primary: '#B4195E',
      secondary: '#FF63A8',
    },
  };

  isCertified = false;
  isCallDashboardEnabled = false;
  isSideColumns = false;

  // Pagination for upcoming events
  upcomingEventsPage = 1;
  upcomingEventsPageSize = 5;

  get upcomingEventsTotalPages(): number {
    if (!this.upcomingEventsList) return 1;
    return Math.ceil(this.upcomingEventsList.length / this.upcomingEventsPageSize);
  }

  get paginatedUpcomingEvents() {
    if (!this.upcomingEventsList) return [];
    const start = (this.upcomingEventsPage - 1) * this.upcomingEventsPageSize;
    return this.upcomingEventsList.slice(start, start + this.upcomingEventsPageSize);
  }

  nextUpcomingEventsPage() {
    if (this.upcomingEventsPage < this.upcomingEventsTotalPages) {
      this.upcomingEventsPage++;
    }
  }

  prevUpcomingEventsPage() {
    if (this.upcomingEventsPage > 1) {
      this.upcomingEventsPage--;
    }
  }

  constructor(
    private event: EventsService,
    public authService: AuthService,
    public userService: UserService,
    private analytics: AnalyticsService,
    private activatedRoute: ActivatedRoute,
    private progressService: ProgressService,
    private tasksService: TasksService,
    private platformService: Platform,
    private router: Router,
  ) {
    this.environment = environment;
    this.platform = this.authService.appMobile ? 'mobile' : 'website';

    this.subscriptions.push(
      this.event.subscribe('login', () => {
        this.userInfoValid = false;
        this.progressValid = false;
      })
    );
    this.subscriptions.push(
      this.event.subscribe('logout', () => this.userInfoValid = false)
    );
    this.subscriptions.push(
      this.event.subscribe('simulator.saveMySimulatorResult', (result) => this.userInfo.simulator = result)
    );
    this.subscriptions.push(
      this.event.subscribe('account.saveMyProfileForm', () => this.userInfoValid = false)
    );
    this.subscriptions.push(
      this.event.subscribe('tasks:updateTaskStatus', () => {
        this.progressValid = false
        this.upcomingEventsValid = false;
      })
    );
    this.subscriptions.push(
      this.event.subscribe('tasks:updateTaskClick', () => {
        this.progressValid = false;
        this.upcomingEventsValid = false;
      })
    );
    this.subscriptions.push(
      this.event.subscribe('coach:updateSessionStatus', () => this.progressValid = false)
    );
    this.subscriptions.push(
      this.event.subscribe('progress:getMyNotifications', (progress) => {
        this.progress = progress;
        this.progressValid = true;
      })
    );
    if (this.userService.userInfo !== null) {
      this.userInfo = this.userService.userInfo;
      this.userInfoValid = true;
      this.initJourneys();
    }

    this.interval = setInterval(() => {
      this.userInfoValid = false;
    }, 10000);
  }

  ngOnInit(): void {
  }

  ionViewWillEnter() {
    this.userType = this.authService.getUserType();

    if (this.userInfo === null || !this.userInfoValid) {
      this.userInfoPromise = this.userService.getUserInfo();
    }

    if (this.progress === null || !this.progressValid) {
      this.progressPromise = this.progressService.getMyNotifications();
    }

    if (this.upcomingEventsList === null || !this.upcomingEventsValid) {
      this.upcomingEventsPromise = this.tasksService.getMyUpcomingEventsList();
    }
  }

  ionViewDidEnter() {
    this.analytics.trackView('Multi-Journey');

    if (!!this.userInfoPromise && (this.userInfo === null || !this.userInfoValid)) {
      this.userInfoPromise.then(data => {
        this.userInfo = data;
        this.userInfoValid = true;
        this.initJourneys();
      });
    }

    if (!!this.progressPromise && (this.progress === null || !this.progressValid)) {
      this.progressPromise.then(data => {
        this.progress = data;
        this.progressValid = true;
      });
    }

    if (!!this.upcomingEventsPromise && (this.upcomingEventsList === null || !this.upcomingEventsValid)) {
      this.upcomingEventsPromise.then(data => {
        this.upcomingEventsList = data.filter((task) => !task.status);
        this.upcomingEventsValid = true;
      });
    }
  }

  initJourneys() {
    this.isCallDashboardEnabled = this.userInfo.company.enabled_aicoach;
    this.socialEnabled = this.authService.getUserCompanyEnabledSocial();
    this.socialUrl = this.authService.getUserCompanySocialUrl();
    this.leaderboardEnabled = this.authService.getUserCompanyEnabledLeaderboard();
    this.searchEnabled = this.userInfo.client_id === 70;

    this.initMeetingPulse();
    this.initSideColumns();

    // Hide menu for veeam
    this.menuVisible = this.userInfo.client_id !== 131 || (this.userInfo.client_id === 131 && this.userInfo.is_manager);
    this.socialBlock = this.userInfo.client_id === 131 && !!this.userInfo.templates.find((t) => t.template.uid === 'SRD');

    this.accreditationTemplate = this.userInfo.templates.find((t) => t.template.uid === 'JPACC') || null;
    this.pmiJournalTemplate = this.userInfo.templates.find((t) => t.template.uid === 'PMIGJ') || null;

    if (!!this.userInfo.dashboard && this.userInfo.dashboard.sections.length > 0) {
      this.userInfo.dashboard.sections.forEach((section, index) => {
        section.template = this.userInfo.templates.find((t) => t.template.uid === section.uid) || null;
        if (section.id === 66) {
          section.title = '<strong>Onboarding</strong>';
        }
        if (section.id === 67) {
          section.title = 'Selling Skills Focus - <strong>Planning & Pipeline</strong> <span class=\'highlight\'>(Months 4-6)</span>';
        }
        if (section.id === 68) {
          section.title = 'Selling Skills Focus - <strong>Discovery & Relationships</strong> <span class=\'highlight\'>(Months 7-9)</span>';
        }
        if (section.id === 69) {
          section.title = 'Selling Skills Focus - <strong>Value Story & Partnership</strong> <span class=\'highlight-light\'>(Months 10-12)</span>';
        }

        if (section.id === 377 || section.id === 384) {
          if (!!this.userInfo.aicoach
            && this.userInfo.aicoach.amanda.status === 'Passed'
            && this.userInfo.aicoach.misha.status === 'Passed') {
            this.isCertified = true;
            section.subtitle = '<strong>Certified</strong>';
            section.background_color = '#21D15F';
          }
        }

        if (section.sections.length > 0) {
          section.sections = section.sections.map((subSection, index) => {
            if ((subSection.id === 379 || subSection.id === 385) && !!this.userInfo.aicoach) {
              subSection.aicoach = this.userInfo.aicoach.amanda;
            }
            if ((subSection.id === 380 || subSection.id === 386) && !!this.userInfo.aicoach) {
              subSection.aicoach = this.userInfo.aicoach.misha;
            }

            subSection.template = this.userInfo.templates.find((t) => t.template.uid === subSection.uid) || null;
            return subSection;
          }).filter((subSection) => !!subSection.template);
        }
      });
    }

    const journey = this.activatedRoute.snapshot.paramMap.get('journey');
    if (!!journey) {
      const section = this.userInfo.dashboard.sections.find(
        (section) => section.template.template.uid === journey
      );
      if (!!section) {
        this.goToJourney(section.template.template_id);
      }
    }
  }

  initMeetingPulse() {
    this.socialUrl = this.authService.getUserCompanySocialUrl();
    if ((!!this.userInfo.templates.find((t) => t.template.id === 325 || t.template.id === 326)
      || this.userInfo.client_id === 70) && this.socialUrl) {
      this.meetingPulse = true;
    } else {
      this.meetingPulse = false;
    }
  }

  initSideColumns() {
    if (this.isCallDashboardEnabled
      || ['jazzpharma', 'veeam', 'pmi'].includes(this.userService.theme)
      || this.userInfo.client_id === 70
    ) {
      this.isSideColumns = true;
    } else {
      this.isSideColumns = false;
    }
  }

  buttonClicked(section) {
    if (section.uid === 'CALLHISTOR' || section.uid === 'SECOCH') {
      this.router.navigate(['/calls/history']);
    } else if (section.uid === 'TAERRA') {
      this.router.navigate(['/game']);
    } else {
      if (section.sections.length > 0) {
        this.toggleButtons(section.id);
      } else if (!!section.template && section.template.template.is_active) {
        this.goToJourney(section.template.template_id);
      }
    }
  }

  toggleButtons(section_id) {
    this.isOpened = this.toggleSectionId === section_id ? !this.isOpened : true;
    this.toggleSectionId = section_id;
  }

  goToJourney(template_id) {
    this.userService.setUserDelegateTemplate(template_id);

    if (template_id === 430) {
      this.router.navigate(['/conference']);
      return;
    }

    const myTemplate = this.userService.getUserDelegateTemplate();

    if (myTemplate.stages.active.length > 0 && myTemplate.template.is_active) {
      this.userService.switchJourney(template_id).then((res) => {
        if (res !== null) {
          this.router.navigate(['/journey', myTemplate.stages.active[0]]);
        }
      });
    }
  }

  goToLeaderboard() {
    this.router.navigate(['/leaderboard']);
  }

  goToSocial() {
    this.router.navigate(['/social']);
  }

  goCallsDashboard() {
    this.router.navigate(['/calls/dashboard']);
  }

  goOne2OneCoaching() {
    this.router.navigate(['/coach/journey']);
  }

  goPmiJournalTemplate() {
    if (this.pmiJournalTemplate !== null) {
      this.goToJourney(this.pmiJournalTemplate.template_id);
    }
  }

  goAccreditation() {
    if (this.accreditationTemplate !== null) {
      this.goToJourney(this.accreditationTemplate.template_id);
    }
  }

  goTasks() {
    this.router.navigate(['/tasks']);
  }

  runTask(task: TasksModel) {
    this.tasksService.runTask(task);
  }

  goToTask(task: TasksModel) {
    // /journey/link/{template_id}/step/{stage_index}#section-{id}
    this.router.navigate(['/journey/link', task.template_id, 'step', task.stage_index], {
      fragment: 'section-' + task.id
    });
  }

  // goToQuiz(session, template) {
  //     if (!!template.stages.active.length) {
  //         this.router.navigate(['/quiz/' + session]);
  //     }
  // }
  //
  // goToSimulatorTDM() {
  //     if (this.templateV6.stages.active.length > 0) {
  //         this.router.navigate(['/simulator/tdm']);
  //     }
  // }
  //
  // goToSimulatorEDM() {
  //     if (!!this.userInfo.simulator.tdm_score && this.templateV6.stages.active.length > 0) {
  //         this.router.navigate(['/simulator/edm']);
  //     }
  // }

  ngOnDestroy(): void {
    if (!!this.interval) {
      clearInterval(this.interval);
    }
    this.subscriptions.forEach(i => i.unsubscribe());
  }
}
